<template>
  <div v-if="isVisible" class="schedule-toolbar">
    <div v-if="isCommentsTab" class="schedule-toolbar__mode">
      <button
        type="button"
        class="schedule-toolbar__mode-button"
        data-test-id="DietDetailComments__SectionGeneral"
        :class="{ 'schedule-toolbar__mode-button--active': activeCommentsSection === 'general' }"
        @click="handleSectionChange('general')"
      >
        <BaseIcon size="18" name="chat" />
        <span>Komentarz ogólny</span>
      </button>
      <button
        type="button"
        class="schedule-toolbar__mode-button"
        data-test-id="DietDetailComments__SectionMeals"
        :class="{ 'schedule-toolbar__mode-button--active': activeCommentsSection === 'meals' }"
        @click="handleSectionChange('meals')"
      >
        <BaseIcon size="18" name="apps" />
        <span>Komentarze do posiłków</span>
      </button>
    </div>
    <div v-else class="schedule-toolbar__mode">
      <button
        type="button"
        class="schedule-toolbar__mode-button"
        data-test-id="DietDetailScheduleToolbar__WeekMode"
        :class="{ 'schedule-toolbar__mode-button--active': isWeekMode }"
        @click="handleWeekMode"
      >
        <BaseIcon size="18" name="view_week" />
        <span>Tydzień</span>
      </button>
      <button
        type="button"
        class="schedule-toolbar__mode-button"
        data-test-id="DietDetailScheduleToolbar__DayMode"
        :class="{ 'schedule-toolbar__mode-button--active': isDayMode }"
        @click="handleDayMode"
      >
        <BaseIcon size="18" name="apps" />
        <span>Dzień</span>
      </button>
    </div>

    <div v-if="showWeekNavigation" class="schedule-toolbar__day-select">
      <button
        type="button"
        class="schedule-toolbar__day-trigger"
        data-test-id="DietDetailScheduleToolbar__WeekSelect"
        :disabled="isWeekSelectDisabled"
        @click.stop="handleToggleSelect"
      >
        <span class="schedule-toolbar__day-dot" />
        <span class="schedule-toolbar__day-label">{{ selectedLabel }}</span>
        <BaseIcon size="18" name="keyboard_arrow_down" />
      </button>
      <div v-if="isSelectOpen" class="schedule-toolbar__day-options">
        <button
          v-for="option in selectOptions"
          :key="option.value"
          type="button"
          class="schedule-toolbar__day-option"
          :data-test-id="`DietDetailScheduleToolbar__WeekOption-${option.value}`"
          :class="{ 'schedule-toolbar__day-option--selected': option.value === selectedValue }"
          @click.stop="handleSelectOption(option.value)"
        >
          {{ option.text }}
        </button>
      </div>
    </div>

    <div
      v-if="showWeekNavigation && isWeekMode && !showAllDays && isWeekMenuOpen"
      class="schedule-toolbar__week-menu"
    >
      <div class="schedule-toolbar__week-menu-options">
        <button
          type="button"
          class="schedule-toolbar__week-menu-option"
          data-test-id="DietDetailScheduleToolbar__CopyWeek"
          @click="handleCopyWeek"
        >
          <BaseIcon size="16" name="filter_none" />
          Skopiuj tydzień
        </button>
        <button
          v-if="canDietBeEdited"
          type="button"
          class="schedule-toolbar__week-menu-option"
          :disabled="!copiedWeekStatus"
          data-test-id="DietDetailScheduleToolbar__PasteWeek"
          @click="handlePasteWeek"
        >
          <BaseIcon size="16" name="queue" />
          Wklej tydzień
        </button>
      </div>
    </div>

    <div v-if="showWeekNavigation" class="schedule-toolbar__nav">
      <button
        type="button"
        class="schedule-toolbar__icon-button"
        data-test-id="DietDetailScheduleToolbar__Previous"
        :disabled="!canGoPrev"
        @click="handlePrev"
      >
        <BaseIcon size="18" name="arrow_back" />
      </button>
      <button
        type="button"
        class="schedule-toolbar__icon-button"
        data-test-id="DietDetailScheduleToolbar__Next"
        :disabled="!canGoNext"
        @click="handleNext"
      >
        <BaseIcon size="18" name="arrow_forward" />
      </button>
    </div>

    <Tooltip v-if="showWeekNavigation && !isDayMode" placement="top">
      <template #trigger>
        <button
          type="button"
          class="schedule-toolbar__icon-button"
          data-test-id="DietDetailScheduleToolbar__AllDays"
          :class="{ 'schedule-toolbar__icon-button--active': showAllDays }"
          @click="handleToggleAllDays"
        >
          <BaseIcon size="18" :name="showAllDays ? 'lock_open' : 'lock'" />
        </button>
      </template>
      Wszystkie dni
    </Tooltip>
  </div>
</template>

<script lang="ts" setup>
  import { useEventListener } from '@vueuse/core'
  import { storeToRefs } from 'pinia'
  import { computed, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  import BaseIcon from '@/components/BaseIcon.vue'
  import { Tooltip } from '@/components/ui'
  import { DIET_WEEK_DAYS_COUNT, getScheduleDayHeader } from '@/const'
  import { useDietDetailProvider } from '@/pages/diets/provider'
  import { useDietsStore } from '@/store/dietsStore'
  import { getDietDaysCount, getDietWeeksCount, getWeekDayCount } from '@/utils/dietDays'

  const TOOLBAR_ROUTE_NAMES = ['DietDetailWeek', 'DietDetailDay', 'DietDetailComments'] as const
  const COMMENT_SECTIONS = ['general', 'meals'] as const
  type CommentSection = (typeof COMMENT_SECTIONS)[number]

  const route = useRoute()
  const router = useRouter()
  const dietStore = useDietsStore()
  const { canDietBeEdited, copiedWeekStatus, diet } = storeToRefs(dietStore)
  const { selectedDayIndex, showAllDays } = useDietDetailProvider()

  const isSelectOpen = ref(false)
  const isWeekMenuOpen = ref(false)

  const routeName = computed(() => route.name?.toString() ?? '')
  const isCommentsTab = computed(() => routeName.value === 'DietDetailComments')
  const activeCommentsSection = computed<CommentSection>(() => {
    const section = route.query.section?.toString() ?? 'general'
    return section === 'meals' ? 'meals' : 'general'
  })
  const isCommentsMealsSection = computed(
    () => isCommentsTab.value && activeCommentsSection.value === 'meals',
  )
  const showWeekNavigation = computed(() => !isCommentsTab.value || isCommentsMealsSection.value)
  const isVisible = computed(() =>
    TOOLBAR_ROUTE_NAMES.includes(routeName.value as (typeof TOOLBAR_ROUTE_NAMES)[number]),
  )
  const isWeekMode = computed(
    () => routeName.value === 'DietDetailWeek' || routeName.value === 'DietDetailComments',
  )
  const isDayMode = computed(() => routeName.value === 'DietDetailDay')

  const daysCount = computed(() => getDietDaysCount(diet.value))
  const weekIndexes = computed(() =>
    Array.from({ length: getDietWeeksCount(daysCount.value) }, (_, index) => index + 1),
  )
  const activeWeek = computed(() => {
    const week = parseInt(route.query.week?.toString() ?? '1', 10)
    return weekIndexes.value.includes(week) ? week : 1
  })

  const isWeekSelectDisabled = computed(() => isWeekMode.value && showAllDays.value)
  const activeWeekStartDayIndex = computed(() => (activeWeek.value - 1) * DIET_WEEK_DAYS_COUNT)
  const activeWeekDaysCount = computed(() => getWeekDayCount(daysCount.value, activeWeek.value))

  const selectedLabel = computed(() => {
    if (isWeekMode.value) return `Tydzień ${activeWeek.value}`

    return getScheduleDayHeader(selectedDayIndex.value, diet.value?.data.start_date).text
  })

  const selectOptions = computed(() => {
    if (isWeekMode.value) {
      return weekIndexes.value.map(week => ({
        text: `Tydzień ${week}`,
        value: week,
      }))
    }

    return Array.from({ length: daysCount.value }, (_, index) => ({
      text: getScheduleDayHeader(index, diet.value?.data.start_date).text,
      value: index,
    }))
  })

  const selectedValue = computed(() =>
    isWeekMode.value ? activeWeek.value : selectedDayIndex.value,
  )

  const canGoPrev = computed(() => {
    if (isWeekSelectDisabled.value) return false
    if (isWeekMode.value) return activeWeek.value > 1
    return selectedDayIndex.value > 0
  })

  const canGoNext = computed(() => {
    if (isWeekSelectDisabled.value) return false
    if (isWeekMode.value) return activeWeek.value < weekIndexes.value.length
    return selectedDayIndex.value < daysCount.value - 1
  })

  const navigateToDay = (dayIndex: number) => {
    const clampedDayIndex = Math.min(Math.max(dayIndex, 0), daysCount.value - 1)
    selectedDayIndex.value = clampedDayIndex
    void router.push({
      name: 'DietDetailDay',
      params: {
        dayIndex: clampedDayIndex,
        dietId: route.params.dietId,
        dietType: route.params.dietType,
      },
    })
  }

  const setActiveWeek = (week: number) => {
    const clampedWeek = weekIndexes.value.includes(week) ? week : 1
    void router.replace({
      query: {
        ...route.query,
        week: clampedWeek === 1 ? undefined : clampedWeek.toString(),
      },
    })
  }

  const handleWeekMode = () => {
    isSelectOpen.value = false
    if (routeName.value === 'DietDetailComments') return

    const dayIndex = Number(route.params.dayIndex ?? selectedDayIndex.value)
    const week = isDayMode.value
      ? Math.floor(dayIndex / DIET_WEEK_DAYS_COUNT) + 1
      : activeWeek.value

    void router.push({
      name: 'DietDetailWeek',
      params: {
        dietId: route.params.dietId,
        dietType: route.params.dietType,
      },
      query: {
        ...route.query,
        week: week === 1 ? undefined : week.toString(),
      },
    })
  }

  const handleDayMode = () => {
    isSelectOpen.value = false
    const selectedDayWeek = Math.floor(selectedDayIndex.value / DIET_WEEK_DAYS_COUNT) + 1
    const dayIndex = isWeekMode.value && !showAllDays.value && selectedDayWeek !== activeWeek.value
      ? activeWeekStartDayIndex.value
      : selectedDayIndex.value
    navigateToDay(dayIndex)
  }

  const handleToggleSelect = () => {
    if (isWeekSelectDisabled.value) return
    isSelectOpen.value = !isSelectOpen.value
  }

  const handleSelectOption = (value: number) => {
    isSelectOpen.value = false
    if (isWeekMode.value) {
      setActiveWeek(value)
      return
    }
    navigateToDay(value)
  }

  const handlePrev = () => {
    if (!canGoPrev.value) return
    if (isWeekMode.value) {
      setActiveWeek(activeWeek.value - 1)
      return
    }
    navigateToDay(selectedDayIndex.value - 1)
  }

  const handleNext = () => {
    if (!canGoNext.value) return
    if (isWeekMode.value) {
      setActiveWeek(activeWeek.value + 1)
      return
    }
    navigateToDay(selectedDayIndex.value + 1)
  }

  const handleToggleAllDays = () => {
    showAllDays.value = !showAllDays.value
    isWeekMenuOpen.value = false
  }

  const handleCopyWeek = () => {
    dietStore.copyWeek(activeWeekStartDayIndex.value, activeWeekDaysCount.value)
    isWeekMenuOpen.value = false
  }

  const handlePasteWeek = () => {
    dietStore.pasteWeek(activeWeekStartDayIndex.value, activeWeekDaysCount.value)
    isWeekMenuOpen.value = false
  }

  const handleSectionChange = (section: CommentSection) => {
    if (section === activeCommentsSection.value) return

    void router.replace({
      query: {
        ...route.query,
        section: section === 'general' ? undefined : section,
      },
    })
  }

  const handleCloseSelect = () => {
    isSelectOpen.value = false
    isWeekMenuOpen.value = false
  }

  useEventListener(document, 'click', handleCloseSelect)
</script>

<style scoped>
  .schedule-toolbar {
    position: sticky;
    top: 0;
    z-index: 12;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    padding: 12px 0;
    background: #fff;
  }

  .schedule-toolbar__mode {
    display: inline-flex;
    overflow: hidden;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
  }

  .schedule-toolbar__mode-button {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    padding: 8px 12px;
    font-family: Montserrat-Medium, sans-serif;
    font-size: 13px;
    line-height: 18px;
    color: var(--color-secondary-text);
    cursor: pointer;
    background: #fff;
    border: 0;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;
  }

  .schedule-toolbar__mode-button:hover {
    background: var(--color-main-10);
  }

  .schedule-toolbar__mode-button--active {
    color: #fff;
    background: var(--color-main-100);
  }

  .schedule-toolbar__mode-button--active:hover {
    color: #fff;
    background: var(--color-main-110);
  }

  .schedule-toolbar__mode-button--active :deep(.base-icon) {
    color: #fff;
  }

  .schedule-toolbar__day-select {
    position: relative;
  }

  .schedule-toolbar__week-menu {
    position: relative;
  }

  .schedule-toolbar__week-menu-options {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 10;
    min-width: 170px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgb(0 0 0 / 8%);
  }

  .schedule-toolbar__week-menu-option {
    display: flex;
    gap: 8px;
    align-items: center;
    width: 100%;
    padding: 8px 12px;
    font-family: Montserrat-Medium, sans-serif;
    font-size: 13px;
    color: var(--color-primary-text);
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: 0;
  }

  .schedule-toolbar__week-menu-option:hover:not(:disabled) {
    background: var(--color-main-10);
  }

  .schedule-toolbar__week-menu-option:disabled {
    color: var(--color-disabled-text);
    cursor: default;
  }

  .schedule-toolbar__day-trigger {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    width: 190px;
    padding: 8px 10px;
    font-family: Montserrat-Medium, sans-serif;
    font-size: 13px;
    line-height: 18px;
    color: var(--color-primary-text);
    cursor: pointer;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
  }

  .schedule-toolbar__day-trigger:disabled {
    cursor: default;
    opacity: 0.35;
  }

  .schedule-toolbar__day-dot {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    background: var(--color-main-100);
    border-radius: 50%;
  }

  .schedule-toolbar__day-label {
    flex: 1;
    text-align: left;
    white-space: nowrap;
  }

  .schedule-toolbar__day-options {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 10;
    min-width: 100%;
    max-height: 280px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgb(0 0 0 / 8%);
  }

  .schedule-toolbar__day-option {
    display: block;
    width: 100%;
    padding: 8px 12px;
    font-family: Montserrat-Medium, sans-serif;
    font-size: 13px;
    color: var(--color-primary-text);
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: 0;
  }

  .schedule-toolbar__day-option:hover {
    background: var(--color-main-10);
  }

  .schedule-toolbar__day-option--selected {
    color: var(--color-main-100);
    background: var(--color-main-30);
  }

  .schedule-toolbar__nav {
    display: inline-flex;
    gap: 4px;
  }

  .schedule-toolbar__icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    color: var(--color-secondary-text);
    cursor: pointer;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
  }

  .schedule-toolbar__icon-button:disabled {
    cursor: default;
    opacity: 0.35;
  }

  .schedule-toolbar__icon-button:hover:not(:disabled) {
    color: var(--color-main-100);
    background: var(--color-main-10);
  }

  .schedule-toolbar__icon-button--active {
    color: var(--color-main-100);
    background: var(--color-main-10);
    border-color: var(--color-main-100);
  }
</style>
